import Cached from '@mui/icons-material/Cached'
import Check from '@mui/icons-material/Check'
import Info from '@mui/icons-material/Info'
import PreviewIcon from '@mui/icons-material/Preview'
import HistoryIcon from '@mui/icons-material/History'
import CloseIcon from '@mui/icons-material/Close'
import DescriptionIcon from '@mui/icons-material/Description'
import LockIcon from '@mui/icons-material/Lock'
import NotesIcon from '@mui/icons-material/Notes'
import { Box, Button, Checkbox, FormControlLabel, Drawer, IconButton, Typography, Tooltip } from '@mui/material'
import { forwardRef, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { useDispatch } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import generatePDF, { Margin } from 'react-to-pdf'
import { toast } from 'react-toastify'
import { getPatientVitalForBookedEvent } from 'src/clinic/components/ClinicManageBookingsPage/utils'
import { VitalsComponentInlineEntryComponent } from 'src/clinic/components/ClinicManageBookingsPage/VitalsComponentInlineEntryComponent'
import { fetchEmrSections } from 'src/clinic/reducers/prescription/services/emrSectionThunk'
import TipTapEditor from '../TherapyReportMakingPage/components/TipTapEditor'
import LSPastVisitReportsComponent from 'src/consumer/components/LiveSessionPage/components/LSPastVisitReportsComponent/LSPastVisitReportsComponent'
import { FLOW_STATUSES_FOR_LEAVING_SESSION } from 'src/consumer/components/LiveSessionPage/utils/sessionNavigationUtils'
import { LoadingSpinner } from 'src/consumer/components/Loaders'
import { GENDER_LIST_ONBOARDING } from 'src/consumer/constants'
import { getAgeFromDobWithTime } from 'src/consumer/helpers/profilePageHelper'
import { DocSideContext } from 'src/context/DocSideContextProvider'
import { useResilientAutoSave } from 'src/customHooks/useResilientAutoSave'
import EmrSectionDataHandler from 'src/mentor/components/EmrRelatedComponents/components/EmrSectionDataHandler'
import useEmrSections from 'src/mentor/components/EmrRelatedComponents/hooks/useEmrSections'
import useEmrSectionsData from 'src/mentor/components/EmrRelatedComponents/hooks/useEmrSectionsData'
import PrintableScreeningReportComponent from 'src/mentor/components/ScreeningReportMakingPage/printableReport/PrintableScreeningReportComponent'
import usePrintableScreeningReportActions from 'src/mentor/components/ScreeningReportMakingPage/printableReport/hooks/usePrintableScreeningReportActions'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessful,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { getMPData, getMPDataWithoutParams, postMPDataWithoutParams } from 'src/mentor/services'
import {
  PresentingComplaintsSection,
  getComplaintsArrayForSubmission as getPresentingComplaintsArrayForSubmission,
  getInitialDataForSettingExistingSelectedComplaints as getInitialPresentingComplaints,
} from '../commonReportComponents/PresentingComplaintsSection'
import {
  PMPButtonsContainer,
  PMPButton,
  PMPSectionHeading,
  PMPSectionInputLabel,
} from '../PrescriptionMakingPage/PrescriptionMakingPageStyles'
import { PatientNotesSectionForReports } from '../PrescriptionMakingPage/PrescriptionMakingPageComponents'
import {
  ModernSectionCard,
  ModernSectionHeading,
  PatientDetailsGrid,
  PatientDetailItem,
  PatientDetailLabel,
  PatientDetailValue,
  FormFieldContainer,
  FormFieldLabel,
  FormFieldRow,
  FormFieldLabelColumn,
  FormFieldInputColumn,
  ModernCheckboxContainer,
  SectionDivider,
  PageContainer,
  DrawerHeader,
  DrawerContent,
} from './ScreeningReportStyles'
import {
  getDateOfScreeningReport,
  getDiagnosisArrayForSubmission,
  getSRInitialDataForSettingExistingSelectedDiagnosis,
  ScreeningReportTextAreaComponent,
} from './ScreeningReportMakingPageComponents'
import {
  ClinicalDiagnosisSelectMenu,
  getClinicalDiagnosisArrayForSubmission,
  getInitialDataForSettingExistingSelectedClinicalDiagnosis,
  getInitialDataForSettingExistingSelectedPrimaryClinicalDiagnosis,
  getClinicalDiagnosisForSubmission,
} from '../TherapyReportMakingPage/TherapyReportMakingPageComponents'
import ModernSectionHeadingTherapy from '../TherapyReportMakingPage/components/ModernSectionHeading'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import VisibilityIcon from '@mui/icons-material/Visibility'
import ReportProblemIcon from '@mui/icons-material/ReportProblem'
import ScreeningSectionsAndQuestions from './screeningSections/ScreeningSectionsAndQuestions'
import { getScreeningSectionsFromDb, HARD_CODED_SCREENING_EMR_SECTION_IDS } from './util'
import ScreeningRecommendationEmrSection from 'src/mentor/components/EmrRelatedComponents/screeningRecommendation/ScreeningRecommendationEmrSection'
import {
  getFreeFormRichTextFromEmrSectionDataList,
  mergeFreeFormRichTextIntoEmrSectionDataList,
} from 'src/mentor/components/EmrRelatedComponents/FreeFormRichText/freeFormRichTextUtils'
import ScreeningReportEmrSettings from './components/ScreeningReportEmrSettings'
const ScreeningReportMakingPage = forwardRef(({
  propEventId,
  propPatientId,
  propClinicId,
  propMentorProfileData,
  propBookedEventInfo,
  triggerSave,
  setTriggerSave,
  afterSaveFunction,
  auxiliaryDataFromProps,
  getProcessAndSetEventData,
  leaveRoom, // NEW: Callback to leave the session
}, ref) => {
  const privateNotesRef = useRef(null)
  const navigate = useNavigate()
  const vitalsRef = useRef(null)
  const dispatch = useDispatch()
  const isLeavingRef = useRef(false) // Track if we're in the process of leaving
  const headerTimeoutRef = useRef(null) // Track the header setting timeout
  const [mentorProfileData, setMentorProfileData] = useState()
  const [mentorProfileDataLoading, setMentorProfileDataLoading] = useState(false)
  const [screeningReportId, setScreeningReportId] = useState(null)
  const targetRef = useRef()
  const urlParams = new URLSearchParams(window.location.search)
  const eventId = propEventId || urlParams.get('eventId')
  const patientId = propPatientId || urlParams.get('patientId')
  const clinicId = propClinicId || urlParams.get('clinicId')
  const [suggestedBookingTherapy, setSuggestedBookingTherapy] = useState(false)
  const [screeningReportSaveLoading, setScreeningReportSaveLoading] = useState(false)

  const [screeningReportData, setScreeningReportData] = useState(null)
  const [observationAndAdvice, setObservationAndAdvice] = useState()
  const [notes, setNotes] = useState('')
  const [freeFormRichText, setFreeFormRichText] = useState('')
  
  // Drawer states for Preview and History
  const [previewDrawerOpen, setPreviewDrawerOpen] = useState(false)
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false)
  // Force refresh counter for preview - increments when drawer opens to ensure latest data
  const [previewRefreshKey, setPreviewRefreshKey] = useState(0)
  // Track content changes to force preview updates
  const [contentVersion, setContentVersion] = useState(0)
  const [selectedComplaints, setSelectedComplaints] = useState()
  const [selectedDiagnosis, setSelectedDiagnosis] = useState()
  const [selectedPrimaryClinicalDiagnosis, setSelectedPrimaryClinicalDiagnosis] = useState(null)
  const [selectedDifferentialClinicalDiagnoses, setSelectedDifferentialClinicalDiagnoses] = useState([])
  const [screeningSections, setScreeningSections] = useState()
  const [screeningQuestionAnswers, setScreeningQuestionAnswers] = useState([])
  const [isPrintingPastHistoryReports, setIsPrintingPastHistoryReports] = useState()
  const [screeningReportTemplate, setScreeningReportTemplate] = useState()
  const [allVitalsOfThisPatient, setAllVitalsOfThisPatient] = useState([])
  const { setFlowId, setButtonTopOffset } = useContext(DocSideContext)

  const { emrSections, loadingEmrSections } = useEmrSections([], 'screening-report')
  const { emrSectionDataList, setEmrSectionDataList } = useEmrSectionsData({ emrSections })
  
  // EMR section print settings state
  const [emrSectionPrintSettings, setEmrSectionPrintSettings] = useState({})
  const emrSettingsRef = useRef(null)
  
  // Track user editing for autosave
  const [userEditing, setUserEditing] = useState(false)
  const lastSavedState = useRef({})
  const [autoSaveStatus, setAutoSaveStatus] = useState({ text: '', status: 'idle' })
  
  // Refs to track latest form values to avoid stale state in autosave
  // These refs are updated immediately when state changes, ensuring autosave always has the latest data
  const latestObservationAndAdviceRef = useRef(observationAndAdvice)
  const latestNotesRef = useRef(notes)
  const latestSelectedComplaintsRef = useRef(selectedComplaints)
  const latestSelectedDiagnosisRef = useRef(selectedDiagnosis)
  const latestSelectedPrimaryClinicalDiagnosisRef = useRef(selectedPrimaryClinicalDiagnosis)
  const latestSelectedDifferentialClinicalDiagnosesRef = useRef(selectedDifferentialClinicalDiagnoses)
  const latestSuggestedBookingTherapyRef = useRef(suggestedBookingTherapy)
  const latestScreeningQuestionAnswersRef = useRef(screeningQuestionAnswers)
  const latestEmrSectionDataListRef = useRef(emrSectionDataList)
  const latestFreeFormRichTextRef = useRef(freeFormRichText)
  
  // Helper function to enrich answers with question data from sections
  function enrichAnswersWithQuestionData(answers, sections) {
    if (!answers || !Array.isArray(answers) || answers.length === 0) {
      return answers || []
    }
    
    if (!sections || !Array.isArray(sections) || sections.length === 0) {
      return answers
    }
    
    // Create a map of questionId -> question and sectionId -> section for quick lookup
    const questionMap = new Map()
    const sectionMap = new Map()
    sections.forEach((section) => {
      // Map section by ID for sectionText lookup
      if (section?.id) {
        sectionMap.set(section.id, section.title)
      }
      // Map questions by ID for questionText lookup
      section?.questions?.forEach((question) => {
        questionMap.set(question.id, { question, sectionTitle: section.title, sectionId: section.id })
      })
    })
    
    // Enrich each answer with question and section data
    return answers.map((answer) => {
      const questionData = questionMap.get(answer?.questionId)
      const sectionText = answer?.sectionId ? (sectionMap.get(answer.sectionId) || questionData?.sectionTitle) : questionData?.sectionTitle
      
      // Always enrich with sectionText and sectionTitle (for backward compatibility)
      const enrichedAnswer = {
        ...answer,
        // Include sectionText (new field) and sectionTitle (for backward compatibility)
        sectionText: answer?.sectionText || sectionText || answer?.sectionTitle,
        sectionTitle: answer?.sectionTitle || sectionText || questionData?.sectionTitle,
      }
      
      // Enrich with question data if missing
      if (questionData) {
        enrichedAnswer.questionText = answer?.questionText || questionData.question?.questionText
        enrichedAnswer.shortQuestionText = answer?.shortQuestionText || questionData.question?.shortQuestionText || questionData.question?.questionText
        enrichedAnswer.translations = answer?.translations || questionData.question?.translations || {}
      }
      
      return enrichedAnswer
    })
  }
  
  // Increment refresh key when drawer opens to force fresh data read
  useEffect(() => {
    if (previewDrawerOpen) {
      setPreviewRefreshKey((prev) => prev + 1)
    }
  }, [previewDrawerOpen])
  
  // Update content version when form data changes (to trigger preview refresh)
  useEffect(() => {
    // Increment content version when key form fields change
    // This ensures preview shows latest data even if drawer is already open
    setContentVersion((prev) => prev + 1)
  }, [
    notes,
    observationAndAdvice?.observation,
    observationAndAdvice?.advice,
    selectedComplaints,
    selectedDiagnosis,
    selectedPrimaryClinicalDiagnosis,
    selectedDifferentialClinicalDiagnoses,
    suggestedBookingTherapy,
    screeningQuestionAnswers,
    emrSectionDataList,
  ])
  
  // Keep refs in sync with state
  useEffect(() => {
    latestObservationAndAdviceRef.current = observationAndAdvice
  }, [observationAndAdvice])
  
  useEffect(() => {
    latestNotesRef.current = notes
  }, [notes])
  
  useEffect(() => {
    latestSelectedComplaintsRef.current = selectedComplaints
  }, [selectedComplaints])
  
  useEffect(() => {
    latestSelectedDiagnosisRef.current = selectedDiagnosis
  }, [selectedDiagnosis])
  
  useEffect(() => {
    latestSelectedPrimaryClinicalDiagnosisRef.current = selectedPrimaryClinicalDiagnosis
  }, [selectedPrimaryClinicalDiagnosis])
  
  useEffect(() => {
    latestSelectedDifferentialClinicalDiagnosesRef.current = selectedDifferentialClinicalDiagnoses
  }, [selectedDifferentialClinicalDiagnoses])
  
  useEffect(() => {
    latestSuggestedBookingTherapyRef.current = suggestedBookingTherapy
  }, [suggestedBookingTherapy])
  
  useEffect(() => {
    latestScreeningQuestionAnswersRef.current = screeningQuestionAnswers
  }, [screeningQuestionAnswers])
  
  useEffect(() => {
    latestEmrSectionDataListRef.current = emrSectionDataList
  }, [emrSectionDataList])

  useEffect(() => {
    latestFreeFormRichTextRef.current = freeFormRichText
  }, [freeFormRichText])

  // Report data for preview/print that always uses latest ref values
  // Calculated on every render to ensure it always has the absolute latest data
  // This ensures preview/print always shows what user is currently typing, even if React state hasn't updated
  // Performance impact is minimal since we're just reading refs and creating an object
  // We create a new object reference every time to ensure React detects the change
  // NOTE: We calculate this on every render (not memoized) to ensure we always get the latest ref values
  // The debounced notes update might not trigger state changes immediately, so memoization would miss updates
  const previewReportData = (() => {
    // Use ref values to get absolute latest data (same approach as autosave)
    const latestObservationAndAdvice = latestObservationAndAdviceRef.current
    const latestNotes = latestNotesRef.current
    const latestSelectedComplaints = latestSelectedComplaintsRef.current
    const latestSelectedDiagnosis = latestSelectedDiagnosisRef.current
    const latestSelectedPrimaryClinicalDiagnosis = latestSelectedPrimaryClinicalDiagnosisRef.current
    const latestSelectedDifferentialClinicalDiagnoses = latestSelectedDifferentialClinicalDiagnosesRef.current
    const latestSuggestedBookingTherapy = latestSuggestedBookingTherapyRef.current
    const latestScreeningQuestionAnswers = latestScreeningQuestionAnswersRef.current
    
    return {
      ...screeningReportData,
      mentorId: mentorProfileData?.id,
      patientId: patientId,
      bookedEventId: eventId,
      content: latestNotes || null, // Always use latest from ref
      id: screeningReportId ?? null,
      observation: latestObservationAndAdvice?.observation || null,
      advice: latestObservationAndAdvice?.advice || null,
      complaints: getPresentingComplaintsArrayForSubmission(latestSelectedComplaints, clinicId),
      diagnosis: getDiagnosisArrayForSubmission(latestSelectedDiagnosis, clinicId),
      primaryClinicalDiagnosis: getClinicalDiagnosisForSubmission(latestSelectedPrimaryClinicalDiagnosis, clinicId),
      differentialClinicalDiagnoses: getClinicalDiagnosisArrayForSubmission(latestSelectedDifferentialClinicalDiagnoses, clinicId),
      suggestedBookingTherapy: latestSuggestedBookingTherapy,
      screeningAnswers: latestScreeningQuestionAnswers,
    }
  })()
  
  // Get latest EMR section data from ref for preview/print
  const latestEmrSectionDataListForPreview = latestEmrSectionDataListRef.current

  // Memoized report data with form data (for backward compatibility and non-preview uses)
  // Note: Preview drawer should use buildScreeningReportDataWithFormData() for latest data
  const screeningReportDataWithFormData = useMemo(() => ({
    ...screeningReportData,
    mentorId: mentorProfileData?.id,
    patientId: patientId,
    bookedEventId: eventId,
    content: notes || null,
    id: screeningReportId ?? null,
    observation: observationAndAdvice?.observation || null,
    advice: observationAndAdvice?.advice || null,
    complaints: getPresentingComplaintsArrayForSubmission(selectedComplaints, clinicId),
    diagnosis: getDiagnosisArrayForSubmission(selectedDiagnosis, clinicId),
    primaryClinicalDiagnosis: getClinicalDiagnosisForSubmission(selectedPrimaryClinicalDiagnosis, clinicId),
    differentialClinicalDiagnoses: getClinicalDiagnosisArrayForSubmission(selectedDifferentialClinicalDiagnoses, clinicId),
    suggestedBookingTherapy: suggestedBookingTherapy,
    screeningAnswers: screeningQuestionAnswers,
  }), [
    screeningReportData,
    mentorProfileData?.id,
    patientId,
    eventId,
    notes,
    screeningReportId,
    observationAndAdvice,
    selectedComplaints,
    selectedDiagnosis,
    selectedPrimaryClinicalDiagnosis,
    selectedDifferentialClinicalDiagnoses,
    suggestedBookingTherapy,
    screeningQuestionAnswers,
    clinicId,
  ])

  // Print actions hook for Save and Print functionality
  // Note: The hook receives previewReportData which uses refs for latest values
  // The actual report data used for printing comes from the PrintableScreeningReportComponent's report prop
  const { printScreening: printScreeningReport } = usePrintableScreeningReportActions({
    setIsPrintingPastHistoryReports,
    isAlwaysPrinting: false,
    sendReportToTriggeringPractitionerApiSignature: '/api/secure/mentor/emr-reports/send-to-triggering-practitioner',
    screeningReport: previewReportData, // Use previewReportData which has latest ref values
    screeningReportContainerRef: targetRef,
    bookedEventInfo: propBookedEventInfo || previewReportData,
  })

  // Helper to compare values (handles null, undefined, objects, arrays)
  const valuesEqual = useCallback((a, b) => {
    if (a === b) return true
    if (a == null && b == null) return true
    if (a == null || b == null) return false
    try {
      return JSON.stringify(a) === JSON.stringify(b)
    } catch {
      return false
    }
  }, [])

  // Function to check if state has changed
  const hasStateChanged = useCallback(() => {
    if (!lastSavedState.current || Object.keys(lastSavedState.current).length === 0) {
      return true
    }
    
    const last = lastSavedState.current
    
    return (
      !valuesEqual(observationAndAdvice, last.observationAndAdvice) ||
      notes !== last.notes ||
      !valuesEqual(selectedComplaints, last.selectedComplaints) ||
      !valuesEqual(selectedDiagnosis, last.selectedDiagnosis) ||
      !valuesEqual(selectedPrimaryClinicalDiagnosis, last.selectedPrimaryClinicalDiagnosis) ||
      !valuesEqual(selectedDifferentialClinicalDiagnoses, last.selectedDifferentialClinicalDiagnoses) ||
      suggestedBookingTherapy !== last.suggestedBookingTherapy ||
      !valuesEqual(screeningQuestionAnswers, last.screeningQuestionAnswers) ||
      !valuesEqual(emrSectionDataList, last.emrSectionDataList)
    )
  }, [
    observationAndAdvice,
    notes,
    selectedComplaints,
    selectedDiagnosis,
    selectedPrimaryClinicalDiagnosis,
    selectedDifferentialClinicalDiagnoses,
    suggestedBookingTherapy,
    screeningQuestionAnswers,
    emrSectionDataList,
    valuesEqual,
  ])

  // Auto-save sequence number to prevent out-of-order saves
  const autosaveSequenceRef = useRef(0)
  
  // Auto-save function
  const performAutoSave = useCallback(async () => {
    // Don't auto-save if already saving manually
    if (screeningReportSaveLoading) {
      return
    }
    
    // Increment sequence number for this save attempt
    const currentSequence = ++autosaveSequenceRef.current
    
    // Build screening report payload for auto-save
    // ALWAYS read from refs at the moment of save to ensure we have the absolute latest data
    // This prevents stale data from being saved, especially during retries
    const latestObservationAndAdvice = latestObservationAndAdviceRef.current
    const latestNotes = latestNotesRef.current
    const latestSelectedComplaints = latestSelectedComplaintsRef.current
    const latestSelectedDiagnosis = latestSelectedDiagnosisRef.current
    const latestSelectedPrimaryClinicalDiagnosis = latestSelectedPrimaryClinicalDiagnosisRef.current
    const latestSelectedDifferentialClinicalDiagnoses = latestSelectedDifferentialClinicalDiagnosesRef.current
    const latestSuggestedBookingTherapy = latestSuggestedBookingTherapyRef.current
    const latestScreeningQuestionAnswers = latestScreeningQuestionAnswersRef.current
    const latestEmrSectionDataList = latestEmrSectionDataListRef.current
    const latestFreeFormRichText = latestFreeFormRichTextRef.current
    
    // Check if state has changed using the latest ref values (not state)
    // This ensures we don't save if user has already made new changes
    const hasChanged = !lastSavedState.current || Object.keys(lastSavedState.current).length === 0 ||
      !valuesEqual(latestObservationAndAdvice, lastSavedState.current.observationAndAdvice) ||
      latestNotes !== lastSavedState.current.notes ||
      !valuesEqual(latestSelectedComplaints, lastSavedState.current.selectedComplaints) ||
      !valuesEqual(latestSelectedDiagnosis, lastSavedState.current.selectedDiagnosis) ||
      !valuesEqual(latestSelectedPrimaryClinicalDiagnosis, lastSavedState.current.selectedPrimaryClinicalDiagnosis) ||
      !valuesEqual(latestSelectedDifferentialClinicalDiagnoses, lastSavedState.current.selectedDifferentialClinicalDiagnoses) ||
      latestSuggestedBookingTherapy !== lastSavedState.current.suggestedBookingTherapy ||
      !valuesEqual(latestScreeningQuestionAnswers, lastSavedState.current.screeningQuestionAnswers) ||
      latestFreeFormRichText !== (lastSavedState.current.freeFormRichText ?? '') ||
      !valuesEqual(latestEmrSectionDataList, lastSavedState.current.emrSectionDataList)
    
    // Don't auto-save if no changes detected
    if (!hasChanged) {
      return
    }
    
    if (vitalsRef.current?.saveVital) {
      vitalsRef.current.saveVital()
    }
    
    // Enrich answers with question data from sections if missing
    const enrichedAnswers = enrichAnswersWithQuestionData(latestScreeningQuestionAnswers, screeningSections)
    
    const screeningReportPayload = {
      bookedEventId: eventId,
      patientId: patientId,
      mentorId: mentorProfileData?.id,
      id: screeningReportId ?? null,
      content: latestNotes || null,
      observation: latestObservationAndAdvice?.observation || null,
      advice: latestObservationAndAdvice?.advice || null,
      // Override apiParam for save API (consistent with TherapyReport pattern)
      complaints: getPresentingComplaintsArrayForSubmission(latestSelectedComplaints, clinicId).map(complaint => ({
        ...complaint,
        apiParam: 'health-complaint__NOTE',
      })),
      diagnosis: getDiagnosisArrayForSubmission(latestSelectedDiagnosis, clinicId).map(diagnosis => ({
        ...diagnosis,
        apiParam: 'diagnosis__NOTE',
      })),
      primaryClinicalDiagnosis: getClinicalDiagnosisForSubmission(latestSelectedPrimaryClinicalDiagnosis, clinicId),
      differentialClinicalDiagnoses: getClinicalDiagnosisArrayForSubmission(latestSelectedDifferentialClinicalDiagnoses, clinicId),
      suggestedBookingTherapy: latestSuggestedBookingTherapy,
      screeningAnswers: enrichedAnswers,
      // Pass array directly - Spring Boot API expects List<String>
      screeningForSpecializations: auxiliaryDataFromProps?.consultationDetails?.doctorSpecializations || null,
      freeFormRichText: latestFreeFormRichText || undefined,
    }
    
    const autosaveEmrList = mergeFreeFormRichTextIntoEmrSectionDataList(
      latestEmrSectionDataList,
      latestFreeFormRichText || '',
    )
    // Use autosave endpoint (similar pattern to therapy report)
    const response = await postMPDataWithoutParams(
      '/api/secure/patient/screening-report/emr/autosave',
      {
        screeningReport: screeningReportPayload,
        emrSectionDataList: autosaveEmrList,
      },
    )
    
    if (response.status === 200 && isCommonResponseSuccessfulV2(response)) {
      // Only update lastSavedState if this is still the latest save attempt
      // This prevents older retries from overwriting newer saves
      // Read current sequence again to check if newer saves have happened
      const latestSequence = autosaveSequenceRef.current
      // If a newer save has started (latestSequence > currentSequence), ignore this outdated save
      if (currentSequence === latestSequence) {
        // Use ref values for consistency (they represent what was actually saved)
        // Re-read refs one more time to ensure we're saving the absolute latest
        const finalObservationAndAdvice = latestObservationAndAdviceRef.current
        const finalNotes = latestNotesRef.current
        const finalSelectedComplaints = latestSelectedComplaintsRef.current
        const finalSelectedDiagnosis = latestSelectedDiagnosisRef.current
        const finalSelectedPrimaryClinicalDiagnosis = latestSelectedPrimaryClinicalDiagnosisRef.current
        const finalSelectedDifferentialClinicalDiagnoses = latestSelectedDifferentialClinicalDiagnosesRef.current
        const finalSuggestedBookingTherapy = latestSuggestedBookingTherapyRef.current
        const finalScreeningQuestionAnswers = latestScreeningQuestionAnswersRef.current
        const finalEmrSectionDataList = latestEmrSectionDataListRef.current
        const finalFreeFormRichText = latestFreeFormRichTextRef.current
        
        lastSavedState.current = {
          observationAndAdvice: finalObservationAndAdvice,
          notes: finalNotes,
          selectedComplaints: finalSelectedComplaints,
          selectedDiagnosis: finalSelectedDiagnosis,
          selectedPrimaryClinicalDiagnosis: finalSelectedPrimaryClinicalDiagnosis,
          selectedDifferentialClinicalDiagnoses: finalSelectedDifferentialClinicalDiagnoses,
          suggestedBookingTherapy: finalSuggestedBookingTherapy,
          screeningQuestionAnswers: finalScreeningQuestionAnswers,
          freeFormRichText: finalFreeFormRichText,
          emrSectionDataList: finalEmrSectionDataList,
        }
        setUserEditing(false)
      } else {
        // This save is outdated, ignore it - newer changes have been made
        console.warn('Ignoring outdated autosave response - newer changes have been made')
        return
      }
      
      // Update report data from response
      const savedScreeningReport = response?.data?.screeningReport || getDataMapFromCommonResponse(response)?.screeningReport
      if (savedScreeningReport) {
        setScreeningReportData(savedScreeningReport)
        setScreeningReportId(savedScreeningReport?.id)
      }
    } else {
      throw new Error(`Auto-save failed with status ${response.status}`)
    }
  }, [
    hasStateChanged,
    screeningReportSaveLoading,
    screeningReportData,
    eventId,
    patientId,
    mentorProfileData?.id,
    screeningReportId,
    notes,
    observationAndAdvice,
    selectedComplaints,
    selectedDiagnosis,
    selectedPrimaryClinicalDiagnosis,
    selectedDifferentialClinicalDiagnoses,
    suggestedBookingTherapy,
    screeningQuestionAnswers,
    clinicId,
    emrSectionDataList,
  ])

  // Use resilient auto-save hook (same pattern as Therapy Report)
  const {
    isAutoSaving,
    autoSaveStatus: resilientAutoSaveStatus,
    retryAttempt,
    nextRetryDelay,
    triggerAutoSave,
    cancelRetry,
    resetAutoSave: resetResilientAutoSave,
  } = useResilientAutoSave(
    performAutoSave,
    hasStateChanged,
    {
      initialDelay: 1500,
      initialRetryDelay: 30000, // 30 seconds
      maxRetryDelay: 300000, // 5 minutes
      backoffMultiplier: 2,
      maxRetries: Infinity,
      enabled: true,
      resetOnSuccess: true,
      onSaveSuccess: () => {
        setAutoSaveStatus({ text: 'Saved', status: 'saved' })
        setTimeout(() => {
          setAutoSaveStatus(prev => {
            if (prev.status === 'saved') {
              return { text: '', status: 'idle' }
            }
            return prev
          })
        }, 3000)
      },
      onSaveError: (error, attempt) => {
        console.error('Screening report auto-save error:', error)
        setAutoSaveStatus({ 
          text: attempt > 0 ? `Retrying... (${attempt + 1})` : 'Save failed', 
          status: 'error' 
        })
      },
    },
  )

  // Create wrapped setters that trigger auto-save
  // Update refs immediately to ensure autosave always has the latest value
  const setObservationAndAdviceWithAutoSave = useCallback((value) => {
    // If value is a function (updater function), we need to get the current value first
    const newValue = typeof value === 'function' 
      ? value(latestObservationAndAdviceRef.current) 
      : value
    
    // Update ref immediately (before state update) to ensure autosave has latest data
    latestObservationAndAdviceRef.current = newValue
    setObservationAndAdvice(newValue)
    setUserEditing(true)
    setContentVersion(prev => prev + 1) // Force preview update
    triggerAutoSave()
  }, [triggerAutoSave])

  // Debounce timeout ref for notes editor
  const notesDebounceTimeoutRef = useRef(null)
  
  const setNotesWithAutoSave = useCallback((value) => {
    const newValue = typeof value === 'function' ? value(latestNotesRef.current) : value
    // Update ref immediately for latest value tracking
    latestNotesRef.current = newValue
    // Update state immediately for UI responsiveness
    setNotes(newValue)
    // Increment content version to force preview updates
    setContentVersion(prev => prev + 1)
    
    // Clear existing debounce timeout
    if (notesDebounceTimeoutRef.current) {
      clearTimeout(notesDebounceTimeoutRef.current)
    }
    
    // Debounce autosave trigger to prevent excessive saves during typing
    notesDebounceTimeoutRef.current = setTimeout(() => {
      setUserEditing(true)
      triggerAutoSave()
      notesDebounceTimeoutRef.current = null
    }, 800) // 800ms debounce - saves after user stops typing for 800ms
  }, [triggerAutoSave])
  
  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (notesDebounceTimeoutRef.current) {
        clearTimeout(notesDebounceTimeoutRef.current)
      }
    }
  }, [])

  const setSelectedComplaintsWithAutoSave = useCallback((value) => {
    const newValue = typeof value === 'function' ? value(latestSelectedComplaintsRef.current) : value
    latestSelectedComplaintsRef.current = newValue
    setSelectedComplaints(newValue)
    setUserEditing(true)
    setContentVersion(prev => prev + 1) // Force preview update
    triggerAutoSave()
  }, [triggerAutoSave])

  const setSelectedDiagnosisWithAutoSave = useCallback((value) => {
    const newValue = typeof value === 'function' ? value(latestSelectedDiagnosisRef.current) : value
    latestSelectedDiagnosisRef.current = newValue
    setSelectedDiagnosis(newValue)
    setUserEditing(true)
    setContentVersion(prev => prev + 1) // Force preview update
    triggerAutoSave()
  }, [triggerAutoSave])

  const setSelectedPrimaryClinicalDiagnosisWithAutoSave = useCallback((value) => {
    const newValue = typeof value === 'function' ? value(latestSelectedPrimaryClinicalDiagnosisRef.current) : value
    latestSelectedPrimaryClinicalDiagnosisRef.current = newValue
    setSelectedPrimaryClinicalDiagnosis(newValue)
    setUserEditing(true)
    setContentVersion(prev => prev + 1) // Force preview update
    triggerAutoSave()
  }, [triggerAutoSave])

  const setSelectedDifferentialClinicalDiagnosesWithAutoSave = useCallback((value) => {
    const newValue = typeof value === 'function' ? value(latestSelectedDifferentialClinicalDiagnosesRef.current) : value
    latestSelectedDifferentialClinicalDiagnosesRef.current = newValue
    setSelectedDifferentialClinicalDiagnoses(newValue)
    setUserEditing(true)
    setContentVersion(prev => prev + 1) // Force preview update
    triggerAutoSave()
  }, [triggerAutoSave])

  const setSuggestedBookingTherapyWithAutoSave = useCallback((value) => {
    const newValue = typeof value === 'function' ? value(latestSuggestedBookingTherapyRef.current) : value
    latestSuggestedBookingTherapyRef.current = newValue
    setSuggestedBookingTherapy(newValue)
    setUserEditing(true)
    setContentVersion(prev => prev + 1) // Force preview update
    triggerAutoSave()
  }, [triggerAutoSave])

  const setScreeningQuestionAnswersWithAutoSave = useCallback((value) => {
    const newValue = typeof value === 'function' ? value(latestScreeningQuestionAnswersRef.current) : value
    latestScreeningQuestionAnswersRef.current = newValue
    setScreeningQuestionAnswers(newValue)
    setUserEditing(true)
    setContentVersion(prev => prev + 1) // Force preview update
    triggerAutoSave()
  }, [triggerAutoSave])

  // Wrapped setEmrSectionDataList to trigger autosave
  const setEmrSectionDataListWithAutoSave = useCallback((value) => {
    const newValue = typeof value === 'function' ? value(latestEmrSectionDataListRef.current) : value
    latestEmrSectionDataListRef.current = newValue
    setEmrSectionDataList(newValue)
    setUserEditing(true)
    // Increment content version to force preview updates
    setContentVersion(prev => prev + 1)
    triggerAutoSave()
  }, [triggerAutoSave, setEmrSectionDataList])

  // Watch for changes in clinical diagnosis fields and trigger autosave
  // This ensures that nested fields like onsetTime, durationSince, durationType, and notes are detected
  // Use a ref to track previous values to avoid unnecessary triggers
  const prevClinicalDiagnosisRef = useRef({
    primary: null,
    differential: null,
  })
  
  useEffect(() => {
    // Only trigger if lastSavedState has been initialized
    if (!lastSavedState.current || Object.keys(lastSavedState.current).length === 0) {
      // Update ref with current values but don't trigger autosave yet
      prevClinicalDiagnosisRef.current = {
        primary: selectedPrimaryClinicalDiagnosis,
        differential: selectedDifferentialClinicalDiagnoses,
      }
      return
    }
    
    // Compare current state with previous ref values to detect actual changes
    const hasChanged = 
      !valuesEqual(selectedPrimaryClinicalDiagnosis, prevClinicalDiagnosisRef.current.primary) ||
      !valuesEqual(selectedDifferentialClinicalDiagnoses, prevClinicalDiagnosisRef.current.differential)
    
    if (hasChanged) {
      // Update ref with current values
      prevClinicalDiagnosisRef.current = {
        primary: selectedPrimaryClinicalDiagnosis,
        differential: selectedDifferentialClinicalDiagnoses,
      }
      
      // Trigger autosave with a delay to ensure state has updated
      const timeoutId = setTimeout(() => {
        triggerAutoSave()
      }, 500)
      return () => clearTimeout(timeoutId)
    }
  }, [selectedPrimaryClinicalDiagnosis, selectedDifferentialClinicalDiagnoses, valuesEqual, triggerAutoSave])

  function renderInfoText() {
    if (resilientAutoSaveStatus.status === 'idle') {
      return <></>
    }

    if (isAutoSaving || screeningReportSaveLoading) {
      return (
        <Box
          sx={{
            color: 'unFocused.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
          }}
        >
          <Cached fontSize="18px" />
          <span>Saving</span>
        </Box>
      )
    }

    if (resilientAutoSaveStatus.status === 'retrying') {
      return (
        <Box
          sx={{
            color: 'warning.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
          }}
        >
          <Cached fontSize="18px" />
          <span>{resilientAutoSaveStatus.text}</span>
        </Box>
      )
    }

    return resilientAutoSaveStatus.status === 'error' ? (
      <Box
        sx={{
          color: 'error.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
        }}
      >
        <Info fontSize="18px" />
        <span>{resilientAutoSaveStatus.text}</span>
      </Box>
    ) : (
      <Box
        sx={{
          color: 'success.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
        }}
      >
        <Check fontSize="18px" />
        <span>{resilientAutoSaveStatus.text}</span>
      </Box>
    )
  }

  async function getAndSetMentorProfileData() {
    if (propMentorProfileData) {
      setMentorProfileData(propMentorProfileData)
      return propMentorProfileData
    } else {
      setMentorProfileDataLoading(true)
      const resp = await getMPData('/api/secure/teacher/profile')
      setMentorProfileDataLoading(false)
      if (resp?.status === 200) {
        setMentorProfileData(resp?.data)
      }
      return resp?.data
    }
  }

  async function processAndSetPageData(
    screeningData,
    emrSectionDataList,
    skipUpdatingData = false,
  ) {
    setScreeningReportData(screeningData)
    setScreeningReportId(screeningData?.id || null)
    if (!skipUpdatingData) {
      // Prepare initial data
      const initialObservationAndAdvice = {
        advice: screeningData?.advice || null,
        observation: screeningData?.observation || null,
      }
      const initialNotes = screeningData?.content || ''
      const initialComplaints = screeningData?.complaints
        ? getInitialPresentingComplaints(screeningData?.complaints)
        : undefined
      const initialDiagnosis = screeningData?.diagnosis
        ? getSRInitialDataForSettingExistingSelectedDiagnosis(screeningData?.diagnosis)
        : undefined
      const initialPrimaryClinicalDiagnosis = screeningData?.primaryClinicalDiagnosis
        ? getInitialDataForSettingExistingSelectedPrimaryClinicalDiagnosis(screeningData?.primaryClinicalDiagnosis)
        : null
      const initialDifferentialClinicalDiagnoses = screeningData?.differentialClinicalDiagnoses
        ? getInitialDataForSettingExistingSelectedClinicalDiagnosis(screeningData?.differentialClinicalDiagnoses)
        : []
      const initialScreeningQuestionAnswers = screeningData?.screeningAnswers || []
      const initialEmrSectionDataList = emrSectionDataList || []
      
      // Update refs BEFORE setting state to prevent hasStateChanged from triggering autosave
      latestObservationAndAdviceRef.current = initialObservationAndAdvice
      latestNotesRef.current = initialNotes
      latestSelectedComplaintsRef.current = initialComplaints
      latestSelectedDiagnosisRef.current = initialDiagnosis
      latestSelectedPrimaryClinicalDiagnosisRef.current = initialPrimaryClinicalDiagnosis
      latestSelectedDifferentialClinicalDiagnosesRef.current = initialDifferentialClinicalDiagnoses
      latestScreeningQuestionAnswersRef.current = initialScreeningQuestionAnswers
      latestEmrSectionDataListRef.current = initialEmrSectionDataList
      
      // Now set state
      setSuggestedBookingTherapy(screeningData?.suggestedBookingTherapy)
      setObservationAndAdvice(initialObservationAndAdvice)
      if (screeningData?.complaints) {
        setSelectedComplaints(initialComplaints)
      }
      if (screeningData?.diagnosis) {
        setSelectedDiagnosis(initialDiagnosis)
      }
      if (screeningData?.primaryClinicalDiagnosis) {
        setSelectedPrimaryClinicalDiagnosis(initialPrimaryClinicalDiagnosis)
      } else {
        setSelectedPrimaryClinicalDiagnosis(null)
      }
      if (screeningData?.differentialClinicalDiagnoses) {
        setSelectedDifferentialClinicalDiagnoses(initialDifferentialClinicalDiagnoses)
      } else {
        setSelectedDifferentialClinicalDiagnoses([])
      }
      if (screeningData?.content) {
        setNotes(initialNotes)
      }
      if (!!emrSectionDataList) {
        setEmrSectionDataList(initialEmrSectionDataList)
      }
      setFreeFormRichText(
        screeningData?.freeFormRichText ||
          getFreeFormRichTextFromEmrSectionDataList(initialEmrSectionDataList) ||
          '',
      )
      setScreeningQuestionAnswers(initialScreeningQuestionAnswers)
      
      // Update lastSavedState to match initial data to prevent autosave from triggering on load
      lastSavedState.current = {
        observationAndAdvice: initialObservationAndAdvice,
        notes: initialNotes,
        selectedComplaints: initialComplaints,
        selectedDiagnosis: initialDiagnosis,
        selectedPrimaryClinicalDiagnosis: initialPrimaryClinicalDiagnosis,
        selectedDifferentialClinicalDiagnoses: initialDifferentialClinicalDiagnoses,
        suggestedBookingTherapy: screeningData?.suggestedBookingTherapy || false,
        screeningQuestionAnswers: initialScreeningQuestionAnswers,
        emrSectionDataList: initialEmrSectionDataList,
      }
    }
  }

  async function getAndSetScreeningReportData() {
    if (auxiliaryDataFromProps?.screeningReport) {
      const mainData = auxiliaryDataFromProps?.screeningReport
      const screeningTemplateData = auxiliaryDataFromProps?.screeningReportTemplate
      const emrSectionData = auxiliaryDataFromProps?.screening__EmrSectionData
      processAndSetPageData(mainData, emrSectionData)
      setScreeningReportTemplate(screeningTemplateData)
      getAndSetAllPatientVital()
      return
    }

    const response = await getMPDataWithoutParams(
      `/api/secure/patient/screening-report/create-by-eventId?eventId=${eventId}`,
    )
    if (response.status === 200 && isCommonResponseSuccessful(response.data.code)) {
      const mainData = response?.data?.data?.screeningReport
      setScreeningReportTemplate(response?.data?.data?.screeningReportTemplate)
      processAndSetPageData(mainData)
    } else {
      toast.error('Error getting screening report data')
    }
  }

  async function getScreeningSections() {
    const sections = await getScreeningSectionsFromDb({
      doctorSpecializations: auxiliaryDataFromProps?.consultationDetails?.doctorSpecializations,
      visitType: auxiliaryDataFromProps?.consultationDetails?.visitNumber > 1 ? 'repeat' : 'new',
    })

    setScreeningSections(sections)
  }

  async function getAndSetAllPatientVital() {
    const patientVitals = await getPatientVitalForBookedEvent(patientId, clinicId, undefined, false)
    setAllVitalsOfThisPatient(patientVitals)
  }

  async function getAndSetEmrSections() {
    // setLoadingEmrSections(true)
    dispatch(fetchEmrSections())

    // const resp = await getMPDataWithoutParams('/api/secure/practitioner/emr-settings/emr-sections')
    // if (
    //   resp?.status === 200 &&
    //   isCommonResponseSuccessful(resp?.data?.code) &&
    //   Array.isArray(resp?.data?.data?.allEmrSections)
    // ) {
    //   setEmrSections(resp?.data?.data?.allEmrSections)
    // }
    // setLoadingEmrSections(false)
  }

  async function setup() {
    const response = await getAndSetMentorProfileData()

    getAndSetEmrSections()
    if (eventId && patientId) {
      await Promise.all([getAndSetScreeningReportData(), getScreeningSections()])
    } else {
      navigate('/404')
    }
  }

  // SIMPLIFIED DESIGN: Child component does NOT manage header state
  // Parent component (MentorClinicSomeChamberAutoJoiningPage) is the single source of truth
  // This component just renders its content - header is managed by parent based on selectedOptionPatientMenu
  // Removed all header-setting logic to prevent race conditions

  useEffect(() => {
    setup()
  }, [eventId, patientId, auxiliaryDataFromProps])


  // Force preview refresh when drawer opens to ensure latest data is shown
  useEffect(() => {
    if (previewDrawerOpen) {
      // Increment refresh key to force component remount with latest data
      setPreviewRefreshKey(prev => prev + 1)
    }
  }, [previewDrawerOpen])

  // Force history drawer refresh when sections load to ensure sections are passed correctly
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0)
  
  useEffect(() => {
    if (historyDrawerOpen) {
      // Force refresh when drawer opens to ensure latest sections are passed
      setHistoryRefreshKey(prev => prev + 1)
    }
  }, [historyDrawerOpen])
  
  useEffect(() => {
    if (historyDrawerOpen) {
      // Force re-render when drawer opens to ensure latest sections are passed
      setHistoryRefreshKey(prev => prev + 1)
    }
  }, [historyDrawerOpen])
  
  useEffect(() => {
    if (historyDrawerOpen && Array.isArray(screeningSections) && screeningSections.length > 0) {
      // Force re-render when sections are loaded and drawer is open
      // Use setTimeout to ensure state update happens after current render
      setTimeout(() => {
        setHistoryRefreshKey(prev => prev + 1)
      }, 0)
    }
  }, [historyDrawerOpen, screeningSections])

  async function saveScreeningReport() {
    if (!screeningReportData?.id && screeningReportSaveLoading) {
      return
    }
    setScreeningReportSaveLoading(true)
    try {
      if (vitalsRef.current?.saveVital) {
        vitalsRef.current.saveVital()
      }
      // Use ref values to ensure we always have the latest data, even if React state hasn't updated yet
      // This fixes the issue where partial input was being sent due to stale state
      const latestObservationAndAdvice = latestObservationAndAdviceRef.current
      const latestNotes = latestNotesRef.current
      const latestSelectedComplaints = latestSelectedComplaintsRef.current
      const latestSelectedDiagnosis = latestSelectedDiagnosisRef.current
      const latestSelectedPrimaryClinicalDiagnosis = latestSelectedPrimaryClinicalDiagnosisRef.current
      const latestSelectedDifferentialClinicalDiagnoses = latestSelectedDifferentialClinicalDiagnosesRef.current
      const latestSuggestedBookingTherapy = latestSuggestedBookingTherapyRef.current
      const latestScreeningQuestionAnswers = latestScreeningQuestionAnswersRef.current
      const latestEmrSectionDataList = latestEmrSectionDataListRef.current
      
      // Explicitly set all fields to ensure observation and other fields are always saved (consistent with TherapyReport pattern)
      const body = {
        bookedEventId: eventId,
        patientId: patientId,
        mentorId: mentorProfileData?.id,
        id: screeningReportId ?? null,
        content: latestNotes || null,
        observation: latestObservationAndAdvice?.observation || null,
        advice: latestObservationAndAdvice?.advice || null,
        // Override apiParam for save API (consistent with TherapyReport pattern)
        complaints: getPresentingComplaintsArrayForSubmission(latestSelectedComplaints, clinicId).map(complaint => ({
          ...complaint,
          apiParam: 'health-complaint__NOTE',
        })),
        diagnosis: getDiagnosisArrayForSubmission(latestSelectedDiagnosis, clinicId).map(diagnosis => ({
          ...diagnosis,
          apiParam: 'diagnosis__NOTE',
        })),
        primaryClinicalDiagnosis: getClinicalDiagnosisForSubmission(latestSelectedPrimaryClinicalDiagnosis, clinicId),
        differentialClinicalDiagnoses: getClinicalDiagnosisArrayForSubmission(latestSelectedDifferentialClinicalDiagnoses, clinicId),
        suggestedBookingTherapy: latestSuggestedBookingTherapy,
screeningAnswers: enrichAnswersWithQuestionData(latestScreeningQuestionAnswers, screeningSections),
        // Pass array directly - Spring Boot API expects List<String>
        screeningForSpecializations: auxiliaryDataFromProps?.consultationDetails?.doctorSpecializations || null,
        freeFormRichText: freeFormRichText || undefined,
      }

      const emrSectionDataListToSave = mergeFreeFormRichTextIntoEmrSectionDataList(
        latestEmrSectionDataList,
        freeFormRichText || '',
      )
      const response = await postMPDataWithoutParams('/api/secure/patient/screening-report/save', {
        screeningReport: body,
        emrSectionDataList: emrSectionDataListToSave,
      })
      if (response.status === 200 && isCommonResponseSuccessfulV2(response)) {
        const dataMap = getDataMapFromCommonResponse(response)
        setScreeningReportData(dataMap?.screeningReport)
        setEmrSectionDataList(dataMap?.emrSectionDataList)
        
        // Update last saved state using ref values (they represent what was actually saved)
        lastSavedState.current = {
          observationAndAdvice: latestObservationAndAdvice,
          notes: latestNotes,
          selectedComplaints: latestSelectedComplaints,
          selectedDiagnosis: latestSelectedDiagnosis,
          selectedPrimaryClinicalDiagnosis: latestSelectedPrimaryClinicalDiagnosis,
          selectedDifferentialClinicalDiagnoses: latestSelectedDifferentialClinicalDiagnoses,
          suggestedBookingTherapy: latestSuggestedBookingTherapy,
          screeningQuestionAnswers: latestScreeningQuestionAnswers,
          emrSectionDataList: latestEmrSectionDataList,
        }
        setUserEditing(false)
        
        await getProcessAndSetEventData({ hideLoader: true })
        toast.success('Saved Screening Report')
      } else {
        toast.error("Couldn't save screening report.")
      }
    } catch (error) {
      console.error('[ScreeningReport Save] Error saving report:', error)
      toast.error("Couldn't save screening report.")
    } finally {
      // Always reset loading state, even if there's an error
      setScreeningReportSaveLoading(false)
    }
  }

  async function saveScreeningReportWithAutoSave(hideToast = false, isRefreshingData = true) {
    if (!screeningReportData?.id && screeningReportSaveLoading) {
      return
    }
    setScreeningReportSaveLoading(true)
    try {
      if (vitalsRef.current?.saveVital) {
        vitalsRef.current.saveVital()
      }
      // Use ref values to ensure we always have the latest data, even if React state hasn't updated yet
      // This fixes the issue where partial input was being sent due to stale state
      const latestObservationAndAdvice = latestObservationAndAdviceRef.current
      const latestNotes = latestNotesRef.current
      const latestSelectedComplaints = latestSelectedComplaintsRef.current
      const latestSelectedDiagnosis = latestSelectedDiagnosisRef.current
      const latestSelectedPrimaryClinicalDiagnosis = latestSelectedPrimaryClinicalDiagnosisRef.current
      const latestSelectedDifferentialClinicalDiagnoses = latestSelectedDifferentialClinicalDiagnosesRef.current
      const latestSuggestedBookingTherapy = latestSuggestedBookingTherapyRef.current
      const latestScreeningQuestionAnswers = latestScreeningQuestionAnswersRef.current
      const latestEmrSectionDataList = latestEmrSectionDataListRef.current
      
      // Explicitly set all fields to ensure observation and other fields are always saved (consistent with TherapyReport pattern)
      const body = {
        bookedEventId: eventId,
        patientId: patientId,
        mentorId: mentorProfileData?.id,
        id: screeningReportId ?? null,
        content: latestNotes || null,
        observation: latestObservationAndAdvice?.observation || null,
        advice: latestObservationAndAdvice?.advice || null,
        // Override apiParam for save API (consistent with TherapyReport pattern)
        complaints: getPresentingComplaintsArrayForSubmission(latestSelectedComplaints, clinicId).map(complaint => ({
          ...complaint,
          apiParam: 'health-complaint__NOTE',
        })),
        diagnosis: getDiagnosisArrayForSubmission(latestSelectedDiagnosis, clinicId).map(diagnosis => ({
          ...diagnosis,
          apiParam: 'diagnosis__NOTE',
        })),
        primaryClinicalDiagnosis: getClinicalDiagnosisForSubmission(latestSelectedPrimaryClinicalDiagnosis, clinicId),
        differentialClinicalDiagnoses: getClinicalDiagnosisArrayForSubmission(latestSelectedDifferentialClinicalDiagnoses, clinicId),
        suggestedBookingTherapy: latestSuggestedBookingTherapy,
        screeningAnswers: enrichAnswersWithQuestionData(latestScreeningQuestionAnswers, screeningSections),
        // Pass array directly - Spring Boot API expects List<String>
        screeningForSpecializations: auxiliaryDataFromProps?.consultationDetails?.doctorSpecializations || null,
        freeFormRichText: freeFormRichText || undefined,
      }
      
      const emrSectionDataListToSaveSecond = mergeFreeFormRichTextIntoEmrSectionDataList(
        latestEmrSectionDataList,
        freeFormRichText || '',
      )
      const response = await postMPDataWithoutParams('/api/secure/patient/screening-report/save', {
        screeningReport: body,
        emrSectionDataList: emrSectionDataListToSaveSecond,
      })
      if (response.status === 200 && isCommonResponseSuccessfulV2(response)) {
        setScreeningReportData(response?.data?.data?.newScreeningReport)
        
        // Update last saved state using ref values (they represent what was actually saved)
        lastSavedState.current = {
          observationAndAdvice: latestObservationAndAdvice,
          notes: latestNotes,
          selectedComplaints: latestSelectedComplaints,
          selectedDiagnosis: latestSelectedDiagnosis,
          selectedPrimaryClinicalDiagnosis: latestSelectedPrimaryClinicalDiagnosis,
          selectedDifferentialClinicalDiagnoses: latestSelectedDifferentialClinicalDiagnoses,
          suggestedBookingTherapy: latestSuggestedBookingTherapy,
          screeningQuestionAnswers: latestScreeningQuestionAnswers,
          emrSectionDataList: latestEmrSectionDataList,
        }
        setUserEditing(false)
        
        if (isRefreshingData) {
          await getProcessAndSetEventData({ hideLoader: true })
        } else {
          processAndSetPageData(response?.data?.data?.newScreeningReport, undefined, true)
        }
        if (!hideToast) {
          toast.success('Saved Screening Report')
        }
      } else {
        if (!hideToast) toast.error("Couldn't save screening report.")
        throw new Error("Couldn't save screening report.")
      }
    } catch (error) {
      console.error('[ScreeningReport Save] Error saving report:', error)
      if (!hideToast) {
        toast.error("Couldn't save screening report.")
      }
      throw error
    } finally {
      // Always reset loading state, even if there's an error
      setScreeningReportSaveLoading(false)
    }
  }

  // NEW: Method to save report and leave session
  // NOTE: This is kept for backward compatibility but should not be called from checkAndOpenModal
  // to avoid circular calls. Use saveScreeningReportWithAutoSave directly instead.
  async function saveAndLeaveScreeningSession(forceMarkAsCompleted = false) {
    await saveScreeningReportWithAutoSave(true, false)
    if (leaveRoom) {
      leaveRoom(forceMarkAsCompleted)
    }
  }

  // NEW: Method to mark as completed without leaving the page
  async function markAsCompletedOnly() {
    // Save the report first
    await saveScreeningReportWithAutoSave(true, false)
    // Mark the event as completed via API (without leaving)
    // The parent component will handle marking the event as completed
    toast.success('Screening Report saved and session marked as completed')
  }

  // NEW: Simple check-and-leave method for screening reports (no next visit validation)
  async function checkAndOpenModal(markAsCompleted = false, shouldLeave = true) {
    if (shouldLeave) {
      // Mark as leaving to prevent header from being re-set
      isLeavingRef.current = true
      
      try {
        // Save the report first
        await saveScreeningReportWithAutoSave(true, false)
        
        // Reset header immediately before leaving to prevent header from persisting
        setFlowId(null)
        setButtonTopOffset(0)
        
        // Don't call leaveRoom here - let the parent handle leaving
        // The parent's handleLeaveRoom will handle the actual leaving logic
        // We just need to return continue to indicate save was successful
        return FLOW_STATUSES_FOR_LEAVING_SESSION.continue
      } catch (error) {
        console.error('[ScreeningReport] Error in checkAndOpenModal:', error)
        // Reset loading state if save failed
        setScreeningReportSaveLoading(false)
        // Still return continue to allow leaving even if save failed
        return FLOW_STATUSES_FOR_LEAVING_SESSION.continue
      }
    } else {
      // Just mark as completed without leaving
      try {
        await markAsCompletedOnly()
        return FLOW_STATUSES_FOR_LEAVING_SESSION.continue
      } catch (error) {
        console.error('[ScreeningReport] Error in markAsCompletedOnly:', error)
        setScreeningReportSaveLoading(false)
        return FLOW_STATUSES_FOR_LEAVING_SESSION.continue
      }
    }
  }

  // Expose methods via ref for parent components
  useImperativeHandle(ref, () => ({
    checkAndOpenModal,
    markAsCompletedOnly,
    saveScreeningReport,
    saveAndPrint: async () => {
      await saveScreeningReport()
      printScreeningReport()
    },
    screeningReportSaveLoading,
    // Auto-save state
    isAutoSaving,
    autoSaveStatus: resilientAutoSaveStatus,
    isSaving: screeningReportSaveLoading || isAutoSaving,
    // Drawer controls
    openPreviewDrawer: () => {
      setPreviewRefreshKey(prev => prev + 1) // Force refresh with new key
      setPreviewDrawerOpen(true)
    },
    openHistoryDrawer: () => setHistoryDrawerOpen(true),
    openSettings: () => {
      if (emrSettingsRef.current?.openDrawer) {
        emrSettingsRef.current.openDrawer()
      }
    },
    openSettingsDrawer: () => {
      if (emrSettingsRef.current?.openDrawer) {
        emrSettingsRef.current.openDrawer()
      }
    },
    // Get latest report data for preview (reads from refs for absolute latest)
    getLatestReportData: () => {
      // Read latest data from refs (same approach as previewReportData)
      const latestObservationAndAdvice = latestObservationAndAdviceRef.current
      const latestNotes = latestNotesRef.current
      const latestSelectedComplaints = latestSelectedComplaintsRef.current
      const latestSelectedDiagnosis = latestSelectedDiagnosisRef.current
      const latestSelectedPrimaryClinicalDiagnosis = latestSelectedPrimaryClinicalDiagnosisRef.current
      const latestSelectedDifferentialClinicalDiagnoses = latestSelectedDifferentialClinicalDiagnosesRef.current
      const latestSuggestedBookingTherapy = latestSuggestedBookingTherapyRef.current
      const latestScreeningQuestionAnswers = latestScreeningQuestionAnswersRef.current
      const latestEmrData = latestEmrSectionDataListRef.current
      
      return {
        report: {
          ...screeningReportData,
          mentorId: mentorProfileData?.id,
          patientId: patientId,
          bookedEventId: eventId,
          content: latestNotes || null,
          id: screeningReportId ?? null,
          observation: latestObservationAndAdvice?.observation || null,
          advice: latestObservationAndAdvice?.advice || null,
          complaints: getPresentingComplaintsArrayForSubmission(latestSelectedComplaints, clinicId),
          diagnosis: getDiagnosisArrayForSubmission(latestSelectedDiagnosis, clinicId),
          primaryClinicalDiagnosis: getClinicalDiagnosisForSubmission(latestSelectedPrimaryClinicalDiagnosis, clinicId),
          differentialClinicalDiagnoses: getClinicalDiagnosisArrayForSubmission(latestSelectedDifferentialClinicalDiagnoses, clinicId),
          suggestedBookingTherapy: latestSuggestedBookingTherapy,
          screeningAnswers: latestScreeningQuestionAnswers,
          // Include vitals if available
          vitals: allVitalsOfThisPatient,
        },
        emrSectionDataList: latestEmrData ? JSON.parse(JSON.stringify(latestEmrData)) : [],
        emrSectionPrintSettings: emrSectionPrintSettings,
        screeningSections: screeningSections,
        templateData: screeningReportTemplate,
      }
    },
  }))

  async function saveAndPrint() {
    await saveScreeningReport()
    printScreeningReport()
  }

  async function saveAndGeneratePDF(type) {
    await saveScreeningReport()
    const filename = `${
      screeningReportData?.patientName && screeningReportData?.patientName
    } ${getDateOfScreeningReport(screeningReportData)} Screening.pdf`
    if (type === 'open') {
      generatePDF(targetRef, {
        filename: filename,
        method: 'open',
        page: { margin: Margin.MEDIUM },
      })
    } else {
      generatePDF(targetRef, {
        filename: filename,
        method: 'save',
        page: { margin: Margin.MEDIUM },
      })
    }
  }

  async function saveTriggerFunction() {
    if (triggerSave) {
      await saveScreeningReport()
      if (setTriggerSave) {
        setTriggerSave(false)
      }
      if (afterSaveFunction) {
        afterSaveFunction()
      }
    }
  }

  useEffect(() => {
    saveTriggerFunction()
  }, [triggerSave])


  // Remove page number indicators
  useEffect(() => {
    const removePageIndicators = () => {
      // Find all elements with fixed position at bottom
      const allElements = document.querySelectorAll('div')
      allElements.forEach((el) => {
        const style = el.getAttribute('style') || window.getComputedStyle(el).cssText
        const text = el.textContent?.trim() || ''
        
        // Check if it's a fixed position element at bottom
        const isFixed = style.includes('position: fixed') || style.includes('position:fixed')
        const hasBottom = style.includes('bottom:') || style.includes('bottom :')
        const hasTransform = style.includes('transform:') || style.includes('transform :')
        const hasPageText = /^Page\s*\d*/i.test(text) || text.toLowerCase().includes('page 1')
        
        // Remove if it matches the criteria
        if (isFixed && (hasBottom || (hasTransform && hasPageText))) {
          el.style.display = 'none'
          el.style.visibility = 'hidden'
          el.style.opacity = '0'
          el.style.height = '0'
          el.style.width = '0'
          el.style.overflow = 'hidden'
        }
      })
    }

    // Run immediately and on interval to catch dynamically added elements
    removePageIndicators()
    const interval = setInterval(removePageIndicators, 500)
    
    return () => clearInterval(interval)
  }, [])

  if (mentorProfileDataLoading) {
    return <LoadingSpinner />
  } else {
    return (
      <>
        <style>
          {`
            /* Hide page number indicators - fixed position elements at bottom */
            div[style*="position: fixed"][style*="bottom"],
            div[style*="position:fixed"][style*="bottom"],
            div[style*="position: fixed"][style*="transform"][style*="translateX"],
            div[style*="position:fixed"][style*="transform"][style*="translateX"] {
              display: none !important;
              visibility: hidden !important;
              opacity: 0 !important;
              height: 0 !important;
              width: 0 !important;
              overflow: hidden !important;
            }
          `}
        </style>
        <PageContainer>
        <div className="hide-on-print-only">

          {/* Save Button Section */}
          <PMPButtonsContainer
            style={{ justifyContent: 'flex-end', position: 'sticky', padding: '0', top: '80px', zIndex: 10 }}
          >
            <div style={{ display: 'flex', justifyContent: 'right', gap: '10px', alignItems: 'center' }}>
              {renderInfoText()}
              <PMPButton whitebg onClick={() => saveAndPrint()}>
                Save and Print
              </PMPButton>
              <PMPButton disabled={screeningReportSaveLoading} onClick={() => saveScreeningReport()}>
                Save {screeningReportSaveLoading && <Spinner size="sm" variant="dark" style={{ marginLeft: '8px' }} />}
              </PMPButton>
            </div>
          </PMPButtonsContainer>

          {/* Patient Details are now shown in the main page header */}
          {/* Quick Action buttons moved to sub-header in parent component */}

          <ModernSectionCard>
            <VitalsComponentInlineEntryComponent
              ref={vitalsRef}
              patientId={propBookedEventInfo?.patientId}
              clinicId={propBookedEventInfo?.clinicId}
              withCreateButton={true}
              bookedEventId={propBookedEventInfo?.id}
              hidePrintButton
              existingVitalData={allVitalsOfThisPatient?.find(
                (item) => item?.bookedEventId === propBookedEventInfo?.id,
              )}
              locallyChangeExistingVitalData={(newVitalForThisBooking) => {
                setAllVitalsOfThisPatient((prev) => {
                  const vitalCopy = prev ? [...prev] : []
                  const vitalsArrayWithoutThisBooking = vitalCopy?.filter(
                    (item) => item?.bookedEventId !== propBookedEventInfo?.id,
                  )
                  return [...vitalsArrayWithoutThisBooking, newVitalForThisBooking]
                })
              }}
            />
          </ModernSectionCard>

          <ModernSectionCard>
            <ScreeningSectionsAndQuestions
              patientInfo={{
                defaultCommunicationLanguage:
                  screeningReportData?.patientDefaultCommunicationLanguage,
              }}
              sections={screeningSections}
              answers={screeningQuestionAnswers}
              setAnswers={setScreeningQuestionAnswersWithAutoSave}
            />
          </ModernSectionCard>

          {/* Presenting Complaint Section */}
          <ModernSectionCard>
            <ModernSectionHeading>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <ReportProblemIcon sx={{ color: '#6779e3', fontSize: 22 }} />
                Presenting Complaint
              </Box>
            </ModernSectionHeading>
            <Box sx={{ width: '100%' }}>
              <PresentingComplaintsSection
                clinicId={clinicId}
                selectedComplaints={selectedComplaints}
                setSelectedComplaints={setSelectedComplaintsWithAutoSave}
              />
            </Box>
          </ModernSectionCard>

          {/* Observations Section */}
          <ModernSectionCard>
            <ModernSectionHeading>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <VisibilityIcon sx={{ color: '#6779e3', fontSize: 22 }} />
                Observations
              </Box>
            </ModernSectionHeading>
            <Box sx={{ width: '100%' }}>
              <ScreeningReportTextAreaComponent
                inputValue={observationAndAdvice?.observation}
                setInputValue={(value) =>
                  setObservationAndAdviceWithAutoSave((prev) => ({ ...prev, observation: value }))
                }
              />
            </Box>
          </ModernSectionCard>

          {/* Primary Clinical Diagnosis Section */}
          <ModernSectionCard>
            <ModernSectionHeading>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <LocalHospitalIcon sx={{ color: '#6779e3', fontSize: 22 }} />
                Primary Clinical Diagnosis
              </Box>
            </ModernSectionHeading>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
              <ClinicalDiagnosisSelectMenu
                clinicId={clinicId}
                isMulti={false}
                selectedClinicalDiagnosis={selectedPrimaryClinicalDiagnosis}
                setSelectedClinicalDiagnosis={(val) => {
                  if (val && !val.note && !val.onsetTime && !val.durationSince) {
                    setSelectedPrimaryClinicalDiagnosisWithAutoSave({
                      ...val,
                      note: '',
                      onsetTime: null,
                      durationSince: '',
                      durationType: 'days',
                    })
                  } else {
                    setSelectedPrimaryClinicalDiagnosisWithAutoSave(val)
                  }
                }}
              />
              {selectedPrimaryClinicalDiagnosis && (selectedPrimaryClinicalDiagnosis?.title || selectedPrimaryClinicalDiagnosis?.label || selectedPrimaryClinicalDiagnosis?.value) && (
                <Box sx={{ 
                  padding: '16px', 
                  backgroundColor: '#f0f4f8', 
                  borderRadius: '12px',
                  border: '2px solid #cbd5e1',
                }}>
                  <Box sx={{ fontWeight: 600, fontSize: '15px', color: '#1e293b', marginBottom: '12px' }}>
                    {selectedPrimaryClinicalDiagnosis?.title || selectedPrimaryClinicalDiagnosis?.label || selectedPrimaryClinicalDiagnosis?.value}
                  </Box>
                  <Box sx={{ marginBottom: '12px' }}>
                    <Box sx={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>Notes</Box>
                    <textarea
                      value={selectedPrimaryClinicalDiagnosis?.note || ''}
                      onChange={(e) => {
                        setSelectedPrimaryClinicalDiagnosisWithAutoSave({
                          ...selectedPrimaryClinicalDiagnosis,
                          note: e.target.value,
                        })
                      }}
                      placeholder="Add notes about this diagnosis..."
                      style={{
                        width: '100%',
                        minHeight: '80px',
                        padding: '10px 12px',
                        border: '2px solid #e2e8f0',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontFamily: 'inherit',
                        resize: 'vertical',
                      }}
                    />
                  </Box>
                </Box>
              )}
            </Box>
          </ModernSectionCard>

          {/* Differential Clinical Diagnoses Section - Only show when Primary Clinical Diagnosis is present OR when Differential Clinical Diagnoses already has data */}
          {((selectedPrimaryClinicalDiagnosis && (selectedPrimaryClinicalDiagnosis?.title || selectedPrimaryClinicalDiagnosis?.label || selectedPrimaryClinicalDiagnosis?.value || selectedPrimaryClinicalDiagnosis?.id)) ||
            (Array.isArray(selectedDifferentialClinicalDiagnoses) && selectedDifferentialClinicalDiagnoses.length > 0)) && (
            <ModernSectionCard>
              <ModernSectionHeading>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <LocalHospitalIcon sx={{ color: '#6779e3', fontSize: 22 }} />
                  Differential Clinical Diagnoses
                </Box>
              </ModernSectionHeading>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
              <ClinicalDiagnosisSelectMenu
                clinicId={clinicId}
                isMulti={true}
                selectedClinicalDiagnosis={selectedDifferentialClinicalDiagnoses}
                setSelectedClinicalDiagnosis={(val) => {
                  if (Array.isArray(val)) {
                    const initialized = val.map(diagnosis => {
                      if (diagnosis && !diagnosis.note && !diagnosis.onsetTime && !diagnosis.durationSince) {
                        return {
                          ...diagnosis,
                          note: '',
                          onsetTime: null,
                          durationSince: '',
                          durationType: 'days',
                        }
                      }
                      return diagnosis
                    })
                    setSelectedDifferentialClinicalDiagnosesWithAutoSave(initialized)
                  } else {
                    setSelectedDifferentialClinicalDiagnosesWithAutoSave(val)
                  }
                }}
              />
              {Array.isArray(selectedDifferentialClinicalDiagnoses) && selectedDifferentialClinicalDiagnoses.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {selectedDifferentialClinicalDiagnoses.map((diagnosis, index) => (
                    <Box 
                      key={diagnosis?.value || diagnosis?.id || index}
                      sx={{ 
                        padding: '16px', 
                        backgroundColor: '#f0f4f8', 
                        borderRadius: '12px',
                        border: '2px solid #cbd5e1',
                      }}
                    >
                      <Box sx={{ fontWeight: 600, fontSize: '15px', color: '#1e293b', marginBottom: '12px' }}>
                        {diagnosis?.title || diagnosis?.label || diagnosis?.value}
                      </Box>
                      <Box sx={{ marginBottom: '12px' }}>
                        <Box sx={{ fontSize: '13px', color: '#64748b', marginBottom: '4px' }}>Notes</Box>
                        <textarea
                          value={diagnosis?.note || ''}
                          onChange={(e) => {
                            const updatedDiagnoses = [...selectedDifferentialClinicalDiagnoses]
                            updatedDiagnoses[index] = {
                              ...diagnosis,
                              note: e.target.value,
                            }
                            setSelectedDifferentialClinicalDiagnosesWithAutoSave(updatedDiagnoses)
                          }}
                          placeholder="Add notes about this diagnosis..."
                          style={{
                            width: '100%',
                            minHeight: '80px',
                            padding: '10px 12px',
                            border: '2px solid #e2e8f0',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontFamily: 'inherit',
                            resize: 'vertical',
                          }}
                        />
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </ModernSectionCard>
          )}

          {/* Advice Section */}
          <ModernSectionCard>
            <ModernSectionHeading>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Info sx={{ color: '#6779e3', fontSize: 22 }} />
                Advice
              </Box>
            </ModernSectionHeading>
            <Box sx={{ width: '100%' }}>
              <ScreeningReportTextAreaComponent
                inputValue={observationAndAdvice?.advice}
                setInputValue={(value) =>
                  setObservationAndAdviceWithAutoSave((prev) => ({ ...prev, advice: value }))
                }
              />
            </Box>
          </ModernSectionCard>

          {/* Screening Report Notes Section */}
          <ModernSectionCard>
            <ModernSectionHeading>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <NotesIcon sx={{ color: '#6779e3', fontSize: 22 }} />
                Screening Report Notes
              </Box>
            </ModernSectionHeading>
            <Box sx={{ width: '100%', minHeight: '500px' }}>
              <TipTapEditor
                value={notes}
                onChange={(html) => setNotesWithAutoSave(html)}
                placeholder="Start writing your screening report notes..."
              />
            </Box>
          </ModernSectionCard>

          {/* EMR Patient History Notes Section */}
          <EmrSectionDataHandler
            emrSection={emrSections?.find((section) => section?.id === 'patient-history-notes')}
            emrSectionDataList={emrSectionDataList}
            setEmrSectionDataList={setEmrSectionDataListWithAutoSave}
            practitionerRole="ROLE_SCREENER"
            runtime={{
              patientId: patientId,
              eventId: eventId,
              clinicId: clinicId,
              screeningReportId: screeningReportId,
            }}
            excludeSubSectionIds={['FREE_NOTES']}
          />

          {/* Screener Recommendation Section (EMR) */}
          {emrSections?.find((section) => section?.id === 'screening-recommendation') && (
            <ScreeningRecommendationEmrSection
              screeningRecommendationEmrSection={emrSections?.find(
                (section) => section?.id === 'screening-recommendation',
              )}
              emrSectionDataList={emrSectionDataList}
              setEmrSectionDataList={setEmrSectionDataListWithAutoSave}
            />
          )}

          {/* Free-form Rich Text Section (EMR) - when enabled in EMR settings */}
          {emrSections?.find((section) => section?.id === 'free_form_rich_text') && (
            <ModernSectionCard>
              <ModernSectionHeading>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <NotesIcon sx={{ color: '#6779e3', fontSize: 22 }} />
                  Free-form Rich Text
                </Box>
              </ModernSectionHeading>
              <Box sx={{ width: '100%', minHeight: '200px' }}>
                <TipTapEditor
                  value={freeFormRichText}
                  onChange={(html) => {
                    setFreeFormRichText(html)
                    setEmrSectionDataList((prev) =>
                      mergeFreeFormRichTextIntoEmrSectionDataList(prev, html),
                    )
                    setUserEditing(true)
                    triggerAutoSave()
                  }}
                  placeholder="Write free-form notes..."
                />
              </Box>
            </ModernSectionCard>
          )}

          {/* Therapy Recommendation - moved below Screener Recommendation */}
          <ModernSectionCard>
            <ModernSectionHeading>Therapy Recommendation</ModernSectionHeading>
            <ModernCheckboxContainer>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={suggestedBookingTherapy}
                    onChange={(e) => {
                      setSuggestedBookingTherapyWithAutoSave(e.target.checked)
                    }}
                    sx={{
                      color: '#6779e3',
                      '&.Mui-checked': {
                        color: '#6779e3',
                      },
                    }}
                  />
                }
                label={
                  <span style={{ fontSize: '15px', fontWeight: 500, color: '#1e293b' }}>
                    Psychotherapy Recommended (and requested)
                  </span>
                }
              />
            </ModernCheckboxContainer>
          </ModernSectionCard>

          {/* Other EMR Sections */}
          {emrSections
            ?.filter((section) => !HARD_CODED_SCREENING_EMR_SECTION_IDS.includes(section?.id))
            .map((section) => (
              <EmrSectionDataHandler
                key={section?.id}
                emrSection={section}
                emrSectionDataList={emrSectionDataList}
                setEmrSectionDataList={setEmrSectionDataListWithAutoSave}
                practitionerRole="ROLE_SCREENER"
                runtime={{
                  patientId: patientId,
                  eventId: eventId,
                  clinicId: clinicId,
                  screeningReportId: screeningReportId,
                }}
              />
            ))}

          {/* Private Notes Section - like Therapy Report page */}
          <ModernSectionCard
            sx={{
              background: 'linear-gradient(135deg, rgba(156, 163, 175, 0.05) 0%, rgba(107, 114, 128, 0.05) 100%)',
              borderColor: 'rgba(156, 163, 175, 0.2)',
            }}
          >
            <ModernSectionHeadingTherapy icon={LockIcon} isPrivate={true}>
              Private Notes
            </ModernSectionHeadingTherapy>
            <Box sx={{ marginTop: '4px' }}>
              <PatientNotesSectionForReports
                ref={privateNotesRef}
                patientName={screeningReportData?.patientName || propBookedEventInfo?.patientName}
                thisBookedEventId={eventId}
                patientId={patientId}
              />
            </Box>
          </ModernSectionCard>

        </div>
        
        {/* Hidden printable report for PDF generation */}
        <div style={{ display: 'none' }}>
          <PrintableScreeningReportComponent
            hideDownloadButton
            report={previewReportData}
            emrSectionDataList={latestEmrSectionDataListForPreview}
            emrSections={emrSections}
            setReport={setScreeningReportData}
            reportContainerRef={targetRef}
            screeningSections={screeningSections}
            hideHeader
            templateData={screeningReportTemplate}
            isAlwaysPrinting={!isPrintingPastHistoryReports}
          />
        </div>
      </PageContainer>

      {/* Preview Report Drawer */}
      <Drawer
        anchor="right"
        open={previewDrawerOpen}
        onClose={() => setPreviewDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: '80%', md: '60%', lg: '50%' },
            maxWidth: '900px',
          },
        }}
      >
        <DrawerHeader>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <DescriptionIcon sx={{ color: '#6779e3', fontSize: 24 }} />
            <Typography
              sx={{
                fontSize: '18px',
                fontWeight: 700,
                color: '#1e293b',
              }}
            >
              Report Preview
            </Typography>
          </Box>
          <IconButton
            onClick={() => setPreviewDrawerOpen(false)}
            sx={{
              color: '#64748b',
              '&:hover': {
                backgroundColor: 'rgba(103, 121, 227, 0.1)',
                color: '#6779e3',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DrawerHeader>
        <DrawerContent>
          {previewDrawerOpen && (() => {
            // Read latest data from refs when drawer opens (ensures we get the absolute latest)
            const latestObservationAndAdvice = latestObservationAndAdviceRef.current
            const latestNotes = latestNotesRef.current
            const latestSelectedComplaints = latestSelectedComplaintsRef.current
            const latestSelectedDiagnosis = latestSelectedDiagnosisRef.current
            const latestSelectedPrimaryClinicalDiagnosis = latestSelectedPrimaryClinicalDiagnosisRef.current
            const latestSelectedDifferentialClinicalDiagnoses = latestSelectedDifferentialClinicalDiagnosesRef.current
            const latestSuggestedBookingTherapy = latestSuggestedBookingTherapyRef.current
            const latestScreeningQuestionAnswers = latestScreeningQuestionAnswersRef.current
            const latestEmrData = latestEmrSectionDataListRef.current
            
            // Build fresh report data from latest refs
            const freshReportData = {
              ...screeningReportData,
              mentorId: mentorProfileData?.id,
              patientId: patientId,
              bookedEventId: eventId,
              content: latestNotes || null,
              id: screeningReportId ?? null,
              observation: latestObservationAndAdvice?.observation || null,
              advice: latestObservationAndAdvice?.advice || null,
              complaints: getPresentingComplaintsArrayForSubmission(latestSelectedComplaints, clinicId),
              diagnosis: getDiagnosisArrayForSubmission(latestSelectedDiagnosis, clinicId),
              primaryClinicalDiagnosis: getClinicalDiagnosisForSubmission(latestSelectedPrimaryClinicalDiagnosis, clinicId),
              differentialClinicalDiagnoses: getClinicalDiagnosisArrayForSubmission(latestSelectedDifferentialClinicalDiagnoses, clinicId),
              suggestedBookingTherapy: latestSuggestedBookingTherapy,
              screeningAnswers: latestScreeningQuestionAnswers,
            }
            
            // Deep clone EMR section data to ensure fresh reference
            const clonedEmrData = latestEmrData ? JSON.parse(JSON.stringify(latestEmrData)) : []
            
            return (
              <>
                <PrintableScreeningReportComponent
                  key={`preview-${previewRefreshKey}-${contentVersion}-${Date.now()}`}
                  hideDownloadButton={false}
                  report={freshReportData}
                  emrSectionDataList={clonedEmrData}
                  emrSectionPrintSettings={emrSectionPrintSettings}
                  emrSections={emrSections}
                  setReport={setScreeningReportData}
                  reportContainerRef={null}
                  screeningSections={screeningSections}
                  hideHeader={false}
                  templateData={screeningReportTemplate}
                  isAlwaysPrinting={false}
                  bookedEventInfo={propBookedEventInfo}
                />
              </>
            )
          })()}
        </DrawerContent>
      </Drawer>

      {/* Patient History Drawer */}
      <Drawer
        anchor="right"
        open={historyDrawerOpen}
        onClose={() => setHistoryDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: '80%', md: '60%', lg: '50%' },
            maxWidth: '900px',
          },
        }}
      >
        <DrawerHeader>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <HistoryIcon sx={{ color: '#6779e3', fontSize: 24 }} />
            <Typography
              sx={{
                fontSize: '18px',
                fontWeight: 700,
                color: '#1e293b',
              }}
            >
              Patient History
            </Typography>
          </Box>
          <IconButton
            onClick={() => setHistoryDrawerOpen(false)}
            sx={{
              color: '#64748b',
              '&:hover': {
                backgroundColor: 'rgba(103, 121, 227, 0.1)',
                color: '#6779e3',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DrawerHeader>
        <DrawerContent>
          {/* Past Visit Reports - Only render when drawer is open to ensure latest sections are passed */}
          {propBookedEventInfo?.patientId && historyDrawerOpen && (() => {
            const sectionsToPass = Array.isArray(screeningSections) ? screeningSections : []
            const componentKey = `history-drawer-open-refresh-${historyRefreshKey}-sections-${sectionsToPass.length}-${sectionsToPass.map(s => s?.id).filter(Boolean).join(',') || 'none'}`
            
            return (
              <LSPastVisitReportsComponent
                key={componentKey}
                patientId={propBookedEventInfo?.patientId}
                referenceBookedEventId={eventId}
                screeningSections={sectionsToPass}
                setIsPrintingPastHistoryReports={setIsPrintingPastHistoryReports}
                doctorSpecializations={auxiliaryDataFromProps?.consultationDetails?.doctorSpecializations}
                visitType={auxiliaryDataFromProps?.consultationDetails?.visitNumber > 1 ? 'repeat' : 'new'}
              />
            )
          })()}
        </DrawerContent>
      </Drawer>

      {/* EMR Sections Settings Drawer */}
      <ScreeningReportEmrSettings
        emrSections={emrSections}
        onSettingsChange={setEmrSectionPrintSettings}
        onOpen={(openFn) => {
          emrSettingsRef.current = openFn
        }}
      />
      </>
    )
  }
})

export default ScreeningReportMakingPage

/* <div
            className="d-flex flex-column"
            style={{
              position: 'relative',
              maxWidth: '794px',
              width: '100%',
              margin: '0 auto',
              padding: '8px 0px 24px',
            }}
          >
            {' '}
            <div
              style={{
                position: 'absolute',
                left: '-85px',
                top: '0',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <div
                className="d-flex flex-column align-items-center justify-content-center"
                style={{
                  background: '#c4b9ff',
                  width: '75px',
                  border: '1px solid #4927ff63',
                }}
              >
                <span
                  style={{
                    width: '100%',
                    textAlign: 'center',
                    background: '#7067bb',
                    color: 'white',
                  }}
                >
                  {/* Visit: {visitNo}
                  Visit: 3
                </span>
                <strong>
                  {dayjs(
                    screeningReportData?.createdTime || screeningReportData?.modifiedTime,
                  ).format('D')}
                </strong>{' '}
                <strong>
                  {dayjs(
                    screeningReportData?.createdTime || screeningReportData?.modifiedTime,
                  ).format('MMM')}
                </strong>
              </div>
              <div style={{ flexGrow: 1, borderLeft: '2px dotted grey', width: '2px' }}></div>
          </div>
            </div> */
